import React, { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  DataGridPro,
  FooterPropsOverrides,
  GridCellModes,
  GridCellParams,
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowClassNameParams,
  GridRowId,
  GridValueGetterParams,
  LoadingOverlayPropsOverrides,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { IDepartmentDTO, IJobDTO, ITimeCategoryBaseDTO, ITimesheetCellDTO } from 'src/api/dtos';
import { useLocalizedJobNames } from 'src/features/jobs';
import {
  useTimesheetData,
  useTimesheetEdit,
  useTimesheetQuery,
  useTimesheetSelection,
} from 'src/features/timesheets/providers';
import { TimesheetExpenseDrawer } from 'src/features/timesheets/TimesheetExpenseDrawer';
import { TimesheetNoteModal } from 'src/features/timesheets/TimesheetNoteModal';
import {
  ChevronFilledDownIcon,
  ChevronFilledUpIcon,
  DataGridLoadingOverlay,
  DataGridNoRowsOverlay,
  getDragDropGridIdField,
} from 'src/shared/components';
import { DAY_DATE_MONTH_FORMAT } from 'src/shared/constants';
import { PermissionsEnums, TimesheetDayStatusEnums } from 'src/shared/enums';
import { usePermissions } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { isAfterDay, isBeforeDay } from 'src/shared/utils';
import { RootState } from 'src/store/types';

import { TimesheetAbsenceOverrideEditCell } from './TimesheetAbsenceOverrideEditCell';
import { TimesheetAbsenceOverrideViewCell } from './TimesheetAbsenceOverrideViewCell';
import { TimesheetDateEditCell } from './TimesheetDateEditCell';
import { TimesheetDateHeaderCell } from './TimesheetDateHeaderCell';
import { TimesheetDateViewCell } from './TimesheetDateViewCell';
import { TimesheetDeleteCell } from './TimesheetDeleteCell';
import { TimesheetDepartmentEditCell } from './TimesheetDepartmentEditCell';
import { TimesheetDepartmentViewCell } from './TimesheetDepartmentViewCell';
import { TimesheetColumnEnums } from './TimesheetGrid.enums';
import { TimesheetGridRow } from './TimesheetGrid.types';
import {
  TIMESHEET_DATE_COLUMN_MIN_WIDTH,
  TIMESHEET_DATE_CELL,
  TIMESHEET_CELL_MENU_ICON,
  TIMESHEET_SELECTED_CELL,
  TIMESHEET_ROW_HEIGHT,
  TIMESHEET_DATE_HEADER,
  isTimesheetCellDisabled,
  isTimesheetRowDisabled,
  isTimesheetDisabled,
  TIMESHEET_DISABLED_GRID,
  TIMESHEET_DISABLED_ROW,
  TIMESHEET_DISABLED_CELL,
  isTimesheetRowEmpty,
  TIMESHEET_LEFT_COLUMNS_MIN_WIDTH,
  TIMESHEET_RIGHT_COLUMNS_MIN_WIDTH,
  TIMESHEET_MIN_ROW_COUNT,
} from './TimesheetGrid.utils';
import { TimesheetGridFooter } from './TimesheetGridFooter';
import { TimesheetJobEditCell } from './TimesheetJobEditCell';
import { TimesheetJobViewCell } from './TimesheetJobViewCell';
import { TimesheetTotalDurationCell } from './TimesheetTotalDurationCell';
import { TimesheetTotalExpenseCell } from './TimesheetTotalExpenseCell';

export const TimesheetGrid = () => {
  const { t } = useTranslate();
  const apiRef = useGridApiRef();
  const { settings } = useSelector((state: RootState) => state.user);
  const numberOfJobLevels = settings?.numberOfJobLevels;
  const localizedJobNames = useLocalizedJobNames(numberOfJobLevels);
  const hasShiftMatchEdit = usePermissions(PermissionsEnums.SHIFTMATCH_EDIT);
  const hasDepartment = usePermissions(PermissionsEnums.FEATUREDEPARTMENT_ALLOCATION_FUNCTION);
  const hasJobCosting = usePermissions(PermissionsEnums.FEATUREJOB_COSTING_FUNCTION);

  const { selectedPayPeriodWeek } = useTimesheetQuery();
  const { selected, onSelect } = useTimesheetSelection();
  const { isInvalid } = useTimesheetData();
  const {
    initialTimesheet,
    editingTimesheet,
    loading,
    onDeleteRow,
    onDepartmentChange,
    onJobChange,
    onAbsenceAndOverrideChange,
    onDurationChange,
  } = useTimesheetEdit();

  const [expenseDrawerOpen, setExpenseDrawerOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [leftColumnWidth, setLeftColumnWidth] = useState(TIMESHEET_LEFT_COLUMNS_MIN_WIDTH);
  const [rightColumnWidth, setRightColumnWidth] = useState(TIMESHEET_RIGHT_COLUMNS_MIN_WIDTH);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const [clipboard, setClipboard] = useState<{ id: GridRowId; field: string; duration: number } | null>(null);

  const hasExpensesColumn = useMemo(
    () => !!initialTimesheet?.rows.some((row) => !!row.totalExpenses),
    [initialTimesheet],
  );

  const leftColumns: GridColDef<TimesheetGridRow>[] = useMemo(() => {
    const departmentColumn: GridColDef<TimesheetGridRow>[] = hasDepartment
      ? [
          {
            field: TimesheetColumnEnums.DEPARTMENT,
            headerName: t('timesheets.department'),
            editable: true,
            sortable: false,
            resizable: false,
            minWidth: TIMESHEET_LEFT_COLUMNS_MIN_WIDTH,
            width: leftColumnWidth,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({
              id,
              field,
              value,
            }: GridRenderCellParams<TimesheetGridRow, IDepartmentDTO | null | undefined>) => {
              const initialValue = initialTimesheet?.rows.find((row) => row.id === id)?.department ?? null;
              const isEdited = !isEqual(initialValue, value ?? null);

              return (
                <TimesheetDepartmentViewCell id={id} field={field} department={value ?? null} isEdited={isEdited} />
              );
            },
            renderEditCell: ({
              id,
              field,
              value,
            }: GridRenderEditCellParams<TimesheetGridRow, IDepartmentDTO | null | undefined>) => {
              return (
                <TimesheetDepartmentEditCell
                  id={id}
                  field={field}
                  department={value ?? null}
                  onDepartmentChange={(department) => onDepartmentChange(id, department)}
                />
              );
            },
          },
        ]
      : [];

    const jobsColumns: GridColDef<TimesheetGridRow>[] = hasJobCosting
      ? [...Array(numberOfJobLevels)].map((_, index) => ({
          field: `${TimesheetColumnEnums.JOBS}-${index}`,
          headerName: localizedJobNames[index],
          editable: true,
          sortable: false,
          resizable: false,
          minWidth: TIMESHEET_LEFT_COLUMNS_MIN_WIDTH,
          width: leftColumnWidth,
          align: 'center',
          headerAlign: 'center',
          renderCell: ({ id, field, row }: GridRenderCellParams<TimesheetGridRow, undefined>) => {
            const jobIndex = Number(field.split('-')[1]); // Extract job index from column field

            if (isNaN(jobIndex)) {
              return null;
            }

            const job = (row.jobs[jobIndex] ?? undefined) as IJobDTO | undefined;
            const initialJob = initialTimesheet?.rows.find((row) => row.id === id)?.jobs[jobIndex] ?? undefined;
            const isEdited = !isEqual(job, initialJob);

            return <TimesheetJobViewCell id={id} field={field} jobValue={job?.values[0]} isEdited={isEdited} />;
          },
          renderEditCell: ({ id, field, row }) => {
            const jobIndex = Number(field.split('-')[1]); // Extract job index from column field

            if (isNaN(jobIndex) || !selectedPayPeriodWeek) {
              return null;
            }

            const jobLevel = jobIndex + 1;
            const job = (row.jobs[jobIndex] ?? undefined) as IJobDTO | undefined;

            return (
              <TimesheetJobEditCell
                id={id}
                field={field}
                startDate={selectedPayPeriodWeek.startDate}
                endDate={selectedPayPeriodWeek.endDate}
                level={jobLevel}
                job={job}
                onJobChange={(job) => onJobChange(id, job)}
              />
            );
          },
        }))
      : [];

    return [
      {
        field: TimesheetColumnEnums.DELETE,
        headerName: '',
        sortable: false,
        resizable: false,
        width: 40,
        align: 'center',
        renderCell: ({ id, row }: GridRenderCellParams<TimesheetGridRow, void>) => {
          return (
            <TimesheetDeleteCell
              onClick={() => onDeleteRow(id)}
              disabled={
                (initialTimesheet && isTimesheetDisabled(initialTimesheet)) ||
                isTimesheetRowDisabled(row) ||
                isTimesheetRowEmpty(row)
              }
            />
          );
        },
      },
      ...departmentColumn,
      ...jobsColumns,
      {
        field: TimesheetColumnEnums.ABSENCES_OVERRIDES,
        headerName: t('timesheets.absencesOverrides'),
        editable: true,
        sortable: false,
        minWidth: TIMESHEET_LEFT_COLUMNS_MIN_WIDTH,
        width: leftColumnWidth,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({
          id,
          field,
          value,
        }: GridRenderCellParams<TimesheetGridRow, ITimeCategoryBaseDTO | null | undefined>) => {
          const initialValue = initialTimesheet?.rows.find((row) => row.id === id)?.absenceAndOverride ?? null;
          const isEdited = !isEqual(initialValue, value ?? null);

          return (
            <TimesheetAbsenceOverrideViewCell
              id={id}
              field={field}
              absenceAndOverride={value ?? null}
              isEdited={isEdited}
            />
          );
        },
        renderEditCell: ({
          id,
          field,
          value,
        }: GridRenderEditCellParams<TimesheetGridRow, ITimeCategoryBaseDTO | null | undefined>) => {
          return (
            <TimesheetAbsenceOverrideEditCell
              id={id}
              field={field}
              absenceAndOverride={value ?? null}
              onAbsenceAndOverrideChange={(absenceAndOverride) => onAbsenceAndOverrideChange(id, absenceAndOverride)}
            />
          );
        },
      },
    ];
  }, [initialTimesheet, hasDepartment, hasJobCosting, numberOfJobLevels, localizedJobNames, leftColumnWidth]);

  const centerColumns: GridColDef<TimesheetGridRow>[] = useMemo(
    () =>
      (initialTimesheet?.columns ?? []).map((column) => ({
        field: column.date,
        headerName: moment(column.date).format(DAY_DATE_MONTH_FORMAT),
        editable: !column.isReadOnly,
        sortable: false,
        resizable: false,
        minWidth: TIMESHEET_DATE_COLUMN_MIN_WIDTH,
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        headerClassName: TIMESHEET_DATE_HEADER,
        cellClassName: TIMESHEET_DATE_CELL,
        valueGetter: ({ row }: GridValueGetterParams<TimesheetGridRow>) => {
          return row.cells.find((cell) => cell.date === column.date);
        },
        renderHeader: () => (
          <TimesheetDateHeaderCell
            date={column.date}
            holiday={column.holidayDescription}
            timeCategory={column.timeCategoryDescription}
            exception={column.exceptions[0]}
          />
        ),
        renderCell: ({
          id,
          field,
          row,
          value,
          isEditable,
        }: GridRenderCellParams<TimesheetGridRow, ITimesheetCellDTO | undefined>) => {
          const initialValue = initialTimesheet?.rows
            .find((row) => row.id === id)
            ?.cells.find((cell) => cell.date === field);
          const isEdited = !isEqual(initialValue, value);
          const isOutsidePayPeriod =
            !!initialValue &&
            !!selectedPayPeriodWeek &&
            (isBeforeDay(initialValue?.date, selectedPayPeriodWeek?.startDate) ||
              isAfterDay(initialValue.date, selectedPayPeriodWeek?.endDate));
          const isAbsenceIgnored =
            row.isScheduledAbsence && column.status === TimesheetDayStatusEnums.HOLIDAY && !!value?.duration;

          return value ? (
            <TimesheetDateViewCell
              id={id}
              field={field}
              value={value}
              isEdited={isEdited}
              disabled={!isEditable}
              isOutsidePayPeriod={isOutsidePayPeriod}
              isAbsenceIgnored={isAbsenceIgnored}
              onAddExpense={handleExpenseDrawerOpen}
              onAddNode={handleNoteModalOpen}
            />
          ) : null;
        },
        renderEditCell: ({
          id,
          field: date,
          value,
          isEditable,
        }: GridRenderEditCellParams<TimesheetGridRow, ITimesheetCellDTO | undefined>) => {
          const initialValue = initialTimesheet?.rows
            .find((row) => row.id === id)
            ?.cells.find((cell) => cell.date === date);
          const isEdited = !isEqual(initialValue, value);
          return value ? (
            <TimesheetDateEditCell
              isEdited={isEdited}
              disabled={!isEditable}
              cell={value}
              onDurationChange={(duration) => onDurationChange(id, date, duration)}
              onAddExpense={handleExpenseDrawerOpen}
              onAddNode={handleNoteModalOpen}
            />
          ) : null;
        },
      })),
    [initialTimesheet, selectedPayPeriodWeek],
  );

  const rightColumns: GridColDef<TimesheetGridRow>[] = useMemo(() => {
    const totalExpensesColumn: GridColDef<TimesheetGridRow>[] = hasExpensesColumn
      ? [
          {
            field: TimesheetColumnEnums.TOTAL_EXPENSES,
            headerName: t('timesheets.totalExpenses'),
            sortable: false,
            resizable: false,
            minWidth: TIMESHEET_RIGHT_COLUMNS_MIN_WIDTH,
            width: rightColumnWidth,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ value }: GridRenderCellParams<TimesheetGridRow, number>) =>
              !!value && <TimesheetTotalExpenseCell expense={value} />,
          },
        ]
      : [];

    return [
      {
        field: TimesheetColumnEnums.TOTAL_HOURS,
        headerName: t('timesheets.totalHours'),
        sortable: false,
        minWidth: TIMESHEET_RIGHT_COLUMNS_MIN_WIDTH,
        width: rightColumnWidth,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }: GridRenderCellParams<TimesheetGridRow, number>) =>
          !!value && <TimesheetTotalDurationCell duration={value} />,
      },
      ...totalExpensesColumn,
    ];
  }, [hasExpensesColumn, rightColumnWidth]);

  const rows = useMemo(() => editingTimesheet?.rows ?? [], [editingTimesheet]);

  const throttleColumnResize = useCallback(
    throttle((side: 'left' | 'right', width: number, numberOfColumns: number) => {
      if (side === 'left') {
        setLeftColumnWidth((prevWidth) => {
          const delta = (width - prevWidth) / numberOfColumns;
          return prevWidth + delta;
        });
      }

      if (side === 'right') {
        setRightColumnWidth((prevWidth) => {
          const delta = (width - prevWidth) / numberOfColumns;
          return prevWidth + delta;
        });
      }
    }, 200),
    [],
  );

  const handleColumnResize: GridEventListener<'columnResize'> = (params) => {
    const field = params.colDef.field;
    const width = params.colDef.width;

    if (field === TimesheetColumnEnums.ABSENCES_OVERRIDES) {
      throttleColumnResize('left', width ?? TIMESHEET_LEFT_COLUMNS_MIN_WIDTH, leftColumns.length - 1);
    }

    if (field === TimesheetColumnEnums.TOTAL_HOURS) {
      throttleColumnResize('right', width ?? TIMESHEET_RIGHT_COLUMNS_MIN_WIDTH, rightColumns.length);
    }
  };

  const isCellEditable = useCallback(
    ({ field, row }: GridCellParams<TimesheetGridRow>) => {
      if (!hasShiftMatchEdit) {
        return false;
      }

      if (initialTimesheet && isTimesheetDisabled(initialTimesheet)) {
        return false;
      }

      if (isTimesheetRowDisabled(row)) {
        return false;
      }

      const isFieldValidDate = moment(field).isValid();

      const cell = isFieldValidDate
        ? (row.cells.find((cell) => cell.date === field) as ITimesheetCellDTO | undefined)
        : undefined;

      if (cell && initialTimesheet && isTimesheetCellDisabled(cell, initialTimesheet.lockDate)) {
        return false;
      }

      return true;
    },
    [hasShiftMatchEdit, initialTimesheet],
  );

  const getRowClassName = useCallback(
    ({ indexRelativeToCurrentPage, row }: GridRowClassNameParams<TimesheetGridRow>) => {
      const classNameList: string[] = [];

      if (indexRelativeToCurrentPage % 2 !== 0) {
        classNameList.push('odd');
      }

      if (initialTimesheet && isTimesheetDisabled(initialTimesheet)) {
        classNameList.push(TIMESHEET_DISABLED_GRID);
      }

      if (isTimesheetRowDisabled(row)) {
        classNameList.push(TIMESHEET_DISABLED_ROW);
      }

      return classNameList.join(' ');
    },
    [initialTimesheet],
  );

  const getCellClassName = useCallback(
    ({ id, field, row, isEditable }: GridCellParams<TimesheetGridRow>) => {
      const classNameList: string[] = [];

      if (selected?.id === id && selected.field === field) {
        classNameList.push(TIMESHEET_SELECTED_CELL);
      }

      const isFieldValidDate = moment(field).isValid();

      const cell = isFieldValidDate
        ? (row.cells.find((cell) => cell.date === field) as ITimesheetCellDTO | undefined)
        : undefined;

      // No need to add disabled className to cell if row is disabled
      if (cell && !isEditable && !isTimesheetRowDisabled(row)) {
        classNameList.push(TIMESHEET_DISABLED_CELL);
      }

      return classNameList.join(' ');
    },
    [selected, initialTimesheet, editingTimesheet],
  );

  const handleExpenseDrawerOpen = () => {
    setExpenseDrawerOpen(true);
  };

  const handleExpenseDrawerClose = () => {
    setExpenseDrawerOpen(false);
  };

  const handleNoteModalOpen = () => {
    setNoteModalOpen(true);
  };

  const handleNoteModalClose = () => {
    setNoteModalOpen(false);
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over) {
        return;
      }

      const sourceIdField = getDragDropGridIdField(active);
      const targetIdField = getDragDropGridIdField(over);

      if (!sourceIdField || !targetIdField || isEqual(sourceIdField, targetIdField)) {
        return;
      }

      const sourceRow = rows.find((row) => row.id === sourceIdField.id);
      const targetRow = rows.find((row) => row.id === targetIdField.id);

      if (!sourceRow || !targetRow) {
        return;
      }

      const sourceCell = sourceRow.cells.find((cell) => cell.date === sourceIdField.field);

      if (!sourceCell || !sourceCell.duration) {
        return;
      }

      // Copy duration from source to target
      onDurationChange(targetIdField.id, targetIdField.field, sourceCell.duration);

      // Update selection to target
      onSelect({ id: targetIdField.id, field: targetIdField.field });
    },
    [rows, onDurationChange, onSelect],
  );

  const handleCellKeyDown = useCallback(
    (params: GridCellParams<TimesheetGridRow>, event: MuiEvent<KeyboardEvent>) => {
      // Handle Enter key for downward navigation with edit mode
      if (event.key === 'Enter') {
        event.preventDefault();
        event.defaultMuiPrevented = true;

        // Get all editable date columns
        const editableColumns = centerColumns.filter((col) => col.editable);
        const currentColIndex = editableColumns.findIndex((col) => col.field === params.field);

        if (currentColIndex !== -1) {
          const currentRowIndex = rows.findIndex((row) => row.id === params.id);
          let nextRowId = params.id;

          // Move to next row
          if (currentRowIndex < rows.length - 1) {
            nextRowId = rows[currentRowIndex + 1].id;
          } else {
            // At last row, do nothing
            return;
          }

          const nextField = editableColumns[currentColIndex].field;
          const nextRow = rows.find((row) => row.id === nextRowId);

          if (nextRow && nextField) {
            // Check if the target cell is editable
            const isEditable = isCellEditable({
              id: nextRowId,
              field: nextField,
              row: nextRow,
            } as GridCellParams<TimesheetGridRow>);

            if (isEditable) {
              // Update selection
              onSelect({ id: nextRowId, field: nextField });

              // Start edit mode
              setTimeout(() => {
                apiRef.current.startCellEditMode({ id: nextRowId, field: nextField });
              }, 0);
            }
          }
        }

        return;
      }

      // Handle Tab key for navigation with edit mode
      if (event.key === 'Tab' && !event.ctrlKey) {
        event.preventDefault();
        event.defaultMuiPrevented = true;

        // Get all editable date columns
        const editableColumns = centerColumns.filter((col) => col.editable);
        const currentColIndex = editableColumns.findIndex((col) => col.field === params.field);

        if (currentColIndex !== -1) {
          let nextRowId = params.id;
          let nextColIndex = event.shiftKey ? currentColIndex - 1 : currentColIndex + 1;

          // Handle navigation direction
          if (event.shiftKey) {
            // Shift+Tab: go to previous column, or previous row's last column
            if (nextColIndex < 0) {
              const currentRowIndex = rows.findIndex((row) => row.id === params.id);
              if (currentRowIndex > 0) {
                nextRowId = rows[currentRowIndex - 1].id;
                nextColIndex = editableColumns.length - 1;
              } else {
                // At first cell, do nothing
                return;
              }
            }
          } else {
            // Tab: go to next column, or next row's first column
            if (nextColIndex >= editableColumns.length) {
              const currentRowIndex = rows.findIndex((row) => row.id === params.id);
              if (currentRowIndex < rows.length - 1) {
                nextRowId = rows[currentRowIndex + 1].id;
                nextColIndex = 0;
              } else {
                // At last cell, do nothing
                return;
              }
            }
          }

          const nextField = editableColumns[nextColIndex].field;
          const nextRow = rows.find((row) => row.id === nextRowId);

          if (nextRow && nextField) {
            // Check if the target cell is editable
            const isEditable = isCellEditable({
              id: nextRowId,
              field: nextField,
              row: nextRow,
            } as GridCellParams<TimesheetGridRow>);

            if (isEditable) {
              // Update selection
              onSelect({ id: nextRowId, field: nextField });

              // Start edit mode
              setTimeout(() => {
                apiRef.current.startCellEditMode({ id: nextRowId, field: nextField });
              }, 0);
            }
          }
        }

        return;
      }

      // Handle Ctrl+C and Ctrl+V
      if (!event.ctrlKey || event.repeat) {
        return;
      }

      // Ensure that "selected" and key down cell are the same
      if (selected?.id !== params.id || selected?.field !== params.field) {
        return;
      }

      switch (event.key) {
        case 'c': {
          event.preventDefault();
          event.defaultMuiPrevented = true;

          const row = rows.find((r) => r.id === params.id);
          const cell = row?.cells.find((c) => c.date === params.field);
          if (cell?.duration) {
            setClipboard({ id: params.id as string, field: params.field, duration: cell.duration });
          }

          break;
        }
        case 'v': {
          event.preventDefault();
          event.defaultMuiPrevented = true;

          if (clipboard) {
            onDurationChange(params.id, params.field, clipboard.duration);
            onSelect({ id: params.id, field: params.field });
          }

          break;
        }
        default:
        // No default
      }
    },
    [selected, rows, clipboard, onDurationChange, onSelect, centerColumns, isCellEditable, apiRef],
  );

  const handleCellClick = useCallback(
    (params: GridCellParams<TimesheetGridRow>) => {
      if (!params.isEditable) {
        return;
      }
      const cellMode = apiRef.current.getCellMode(params.id, params.field);
      if (cellMode !== GridCellModes.Edit) {
        onSelect({ id: params.id, field: params.field });
        apiRef.current.startCellEditMode({ id: params.id, field: params.field });
      }
    },
    [apiRef],
  );
  // Cell selection and navigation
  useEffect(() => {
    const handleCellFocusIn: GridEventListener<'cellFocusIn'> = ({
      id,
      field,
      row,
    }: GridCellParams<TimesheetGridRow>) => {
      const isFieldValidDate = moment(field).isValid();

      if (!isFieldValidDate) {
        return;
      }

      const cell = row.cells.find((cell) => cell.date === field) as ITimesheetCellDTO | undefined;

      if (!cell) {
        return;
      }

      onSelect({ id, field });
    };

    return apiRef.current?.subscribeEvent('cellFocusIn', handleCellFocusIn, { isFirst: true });
  }, [apiRef, selected, onSelect]);

  return (
    <>
      <Box sx={{ height: isInvalid || !rows.length ? TIMESHEET_ROW_HEIGHT * 7 : undefined, position: 'relative' }}>
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <StyledDataGrid
            apiRef={apiRef}
            columns={[...leftColumns, ...centerColumns, ...rightColumns]}
            rows={isInvalid ? [] : rows}
            loading={loading}
            // Editing
            isCellEditable={isCellEditable}
            // Columns
            onColumnResize={handleColumnResize}
            // Key down
            onCellKeyDown={handleCellKeyDown}
            onCellClick={handleCellClick}
            // Custom components
            slots={{
              columnSortedAscendingIcon: ChevronFilledUpIcon,
              columnSortedDescendingIcon: ChevronFilledDownIcon,
              noRowsOverlay: DataGridNoRowsOverlay,
              loadingOverlay: DataGridLoadingOverlay,
              footer: TimesheetGridFooter,
            }}
            slotProps={{
              loadingOverlay: {
                isEmpty: editingTimesheet && !editingTimesheet.rows.length,
                skeletonHeight: TIMESHEET_ROW_HEIGHT,
                skeletonRows: TIMESHEET_MIN_ROW_COUNT,
              } as LoadingOverlayPropsOverrides,
              footer: {
                leftColumnFields: leftColumns.map((column) => column.field),
                rightColumnFields: rightColumns.map((column) => column.field),
                hasExpensesColumn,
              } as FooterPropsOverrides,
            }}
            // Settings
            disableColumnMenu
            disableColumnReorder
            disableRowSelectionOnClick
            disableMultipleRowSelection
            // Styles
            autoHeight={!isInvalid && !!rows.length}
            getRowClassName={getRowClassName}
            getCellClassName={getCellClassName}
            columnHeaderHeight={TIMESHEET_ROW_HEIGHT}
            rowHeight={TIMESHEET_ROW_HEIGHT}
            showColumnVerticalBorder
            showCellVerticalBorder
            pinnedColumns={{
              left: leftColumns.map((column) => column.field),
              right: rightColumns.map((column) => column.field),
            }}
          />
        </DndContext>
        {isInvalid && (
          <Alert
            severity="error"
            sx={{ width: 460, position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
          >
            <Stack gap={1}>
              <Typography fontWeight={600}>{t('timesheets.employeeIsNotATimesheetEmployee')}</Typography>
              <Typography fontSize="0.875rem" color="text.secondary">
                {t('global.pleaseContactYourSystemAdministrator')}
              </Typography>
            </Stack>
          </Alert>
        )}
      </Box>
      <TimesheetExpenseDrawer open={expenseDrawerOpen} onClose={handleExpenseDrawerClose} />
      <TimesheetNoteModal open={noteModalOpen} onClose={handleNoteModalClose} />
    </>
  );
};

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  border: 'none',
  // GRID
  '.MuiDataGrid-main': {
    borderRadius: '8px 8px 0 0',
    backgroundColor: theme.palette.background.paper,
    outline: `1px solid ${theme.palette.primary.main}`,
  },
  // HEADER
  '.MuiDataGrid-columnHeaders': {
    borderColor: theme.palette.primary.main,
  },
  '.MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1,
    textAlign: 'center',
  },
  '.MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '.MuiDataGrid-pinnedColumnHeaders--left': {
    boxShadow: `1px 0 ${theme.palette.primary.main}`,
    backgroundImage: 'none',
  },
  '.MuiDataGrid-pinnedColumnHeaders--right': {
    boxShadow: `-1px 0 ${theme.palette.primary.main}`,
    backgroundImage: 'none',
  },
  '.MuiDataGrid-columnHeadersInner': {
    backgroundColor: theme.palette.background.default,
  },
  [`.${TIMESHEET_DATE_HEADER}`]: {
    padding: 0,
    '.MuiDataGrid-columnHeaderTitleContainerContent': {
      flex: 1,
    },
  },
  // COLUMNS
  '.MuiDataGrid-virtualScrollerRenderZone': {
    backgroundImage:
      theme.palette.mode === 'dark'
        ? `linear-gradient(${theme.palette.secondary.main}20, ${theme.palette.secondary.main}20)`
        : undefined,
  },
  '.MuiDataGrid-pinnedColumns': {
    backgroundImage: 'none',
  },
  // ROW
  '.MuiDataGrid-row.odd': {
    backgroundImage: `linear-gradient(${theme.palette.primary.main}0c, ${theme.palette.primary.main}0c)`,
  },
  // CELL
  '.MuiDataGrid-pinnedColumns--left': {
    boxShadow: `1px 0 ${theme.palette.primary.main}`,
  },
  '.MuiDataGrid-pinnedColumns--right': {
    boxShadow: `-1px 0 ${theme.palette.primary.main}`,
  },
  '.MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within,': {
    outline: 'none',
  },
  '.MuiDataGrid-cell': {
    padding: 0,
  },
  [`.${TIMESHEET_DATE_CELL}`]: {
    padding: 0,
  },
  [`.${TIMESHEET_SELECTED_CELL}`]: {
    border: `1px solid ${theme.palette.primary.main} !important`,
    [`.${TIMESHEET_CELL_MENU_ICON}`]: {
      visibility: 'visible',
    },
  },
  // DISABLED
  [`.${TIMESHEET_DISABLED_GRID}, .${TIMESHEET_DISABLED_ROW}, .${TIMESHEET_DISABLED_CELL}`]: {
    // Disabled style should take priority
    backgroundImage: `linear-gradient(${theme.palette.grey[200]}80, ${theme.palette.grey[200]}80) !important`,
    p: { color: `${theme.palette.text.disabled} !important` },
  },
})) as typeof DataGridPro; // Casting required to resolve TypeScript issue: https://github.com/mui/mui-x/issues/6014;
